
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, CheckCircle, XCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Course } from "@/types/course";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useQueryClient } from "@tanstack/react-query";

interface CourseHeaderProps {
  course: Course;
  isPublishing: boolean;
  setIsPublishing: (value: boolean) => void;
  profile?: { username: string } | null;
}

export const CourseHeader = ({ course, isPublishing, setIsPublishing, profile }: CourseHeaderProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handlePublishToggle = async () => {
    if (!course || !course.id) return;
    
    try {
      setIsPublishing(true);
      
      const newPublishedState = !course.published;
      
      const { error } = await supabase
        .from('courses')
        .update({ 
          published: newPublishedState 
        } as any)
        .eq('id', course.id);
        
      if (error) throw error;
      
      queryClient.invalidateQueries({
        queryKey: ['course', course.id],
      });
      
      toast.success(newPublishedState ? 'Course published successfully' : 'Course unpublished');
      
    } catch (error) {
      console.error('Error toggling course publish state:', error);
      toast.error('Failed to update course publish state');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleViewCourse = () => {
    if (course && profile?.username && course.slug) {
      const publicCourseUrl = `/${profile.username}/${course.slug}`;
      
      console.log("[CourseHeader] Navigating to course preview:", {
        username: profile.username,
        slug: course.slug,
        path: publicCourseUrl
      });
      
      // Open the course public page in a new tab
      window.open(publicCourseUrl, '_blank', 'noopener,noreferrer');
    } else {
      console.error("[CourseHeader] Cannot navigate to preview:", {
        hasUsername: !!profile?.username,
        hasCourse: !!course,
        hasSlug: !!course?.slug
      });
      
      // Show appropriate error message
      if (!profile?.username) {
        toast.error("You need to set up your username first");
      } else if (!course?.slug) {
        toast.error("Course slug is missing. Please save course details first.");
      }
    }
  };

  return (
    <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/dashboard/courses')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <h1 className="text-2xl font-bold">{course.title}</h1>
          {course.published ? (
            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400">
              <CheckCircle className="h-3 w-3 mr-1" /> Published
            </div>
          ) : (
            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-800/20 dark:text-yellow-400">
              <XCircle className="h-3 w-3 mr-1" /> Draft
            </div>
          )}
        </div>
        <p className="text-muted-foreground mt-1 line-clamp-1">
          {course.description}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleViewCourse}
                disabled={!profile?.username || !course.slug}
              >
                <Eye className="h-4 w-4 mr-2" /> Preview
              </Button>
            </TooltipTrigger>
            <TooltipContent>Preview how your course looks to students</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant={course.published ? "secondary" : "default"}
                size="sm"
                onClick={handlePublishToggle}
                disabled={isPublishing}
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {course.published ? "Unpublishing..." : "Publishing..."}
                  </>
                ) : (
                  <>
                    {course.published ? (
                      <>
                        <XCircle className="h-4 w-4 mr-2" /> Unpublish
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" /> Publish
                      </>
                    )}
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {course.published 
                ? "Hide this course from your public page" 
                : "Make this course visible on your public page"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};
